import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Send, Check } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import { DIAGNOSTIC_DIMS } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL || "";
const DIM_ORDER = ["process", "people", "technology", "governance", "automation"];

export default function DiagnosticResult() {
  useTitle("Your Result — Operating Reality");
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | result | share | shared | error
  const [data, setData] = useState(null);
  const [shareForm, setShareForm] = useState({ recipient_email: "", recipient_name: "", message: "I completed this Operating Reality diagnostic and thought the result might be useful for us to discuss.", website: "" });
  const [shareStatus, setShareStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    if (!token) { setState("error"); return; }
    fetch(`${API}/api/diagnostic/result/${token}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setState("result"); })
      .catch(() => setState("error"));
  }, [token]);

  const submitShare = async (e) => {
    e.preventDefault();
    if (!shareForm.recipient_email.trim()) return;
    if (shareForm.website) return; // honeypot
    setShareStatus("sending");
    try {
      const res = await fetch(`${API}/api/diagnostic/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, ...shareForm }),
      });
      setShareStatus(res.ok ? "sent" : "error");
    } catch { setShareStatus("error"); }
  };

  if (state === "loading") return (
    <main className="wrap flex min-h-screen flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-glow" />
      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Loading your result</p>
    </main>
  );

  if (state === "error") return (
    <main className="wrap flex min-h-screen flex-col items-start justify-center pt-24">
      <p className="kicker">Result not found</p>
      <h1 className="mt-6 text-3xl font-medium tracking-tight text-slate-100">This result link is invalid or has expired.</h1>
      <p className="mt-4 max-w-md text-sm text-slate-400">The link may have expired or been entered incorrectly. You can take the diagnostic again to generate a new result.</p>
      <Link to="/diagnostic" className="btn-primary mt-10">Take the diagnostic <ArrowRight size={14} /></Link>
    </main>
  );

  const result = data?.result;
  const top = result?.top_dimension;
  const scores = DIM_ORDER.map(dim => ({
    dim,
    total: result?.dimensions?.[dim]?.score || 0,
    max: 15,
    label: result?.dimensions?.[dim]?.label || "",
    summary: result?.dimensions?.[dim]?.summary || "",
  }));

  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="diagnostic-result-page">
      <section className="relative min-h-[calc(100vh-72px)] overflow-hidden">
        <div className="bg-grid absolute inset-0 opacity-25" />
        <div className="wrap relative mx-auto w-full max-w-3xl py-20">

          {/* Score + top dimension */}
          <Reveal>
            <p className="kicker">Your Operating Reality Result</p>
            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-mono text-6xl font-semibold tracking-tight text-slate-100 lg:text-7xl" data-testid="result-score">
                {result?.overall_score ?? "—"}
              </span>
              <span className="font-mono text-2xl text-slate-500">/ 75</span>
            </div>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-glow/60">
              {result?.overall_label}
            </p>
            {top && (
              <>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">Primary area of attention</p>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-slate-100 lg:text-4xl">
                  {DIAGNOSTIC_DIMS?.[top]?.label}
                </h1>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400">
                  {result?.dimensions?.[top]?.summary || DIAGNOSTIC_DIMS?.[top]?.narrative}
                </p>
              </>
            )}
          </Reveal>

          {/* Dimension bars */}
          <div className="mt-14 space-y-6">
            {scores.map((s, i) => (
              <Reveal key={s.dim} delay={i * 0.08}>
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-xs uppercase tracking-[0.22em] text-slate-300">{DIAGNOSTIC_DIMS?.[s.dim]?.label}</span>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-slate-600">
                      {s.label && <span className="mr-3 text-slate-500">{s.label}</span>}
                      {s.total} / {s.max}
                    </span>
                  </div>
                  <div className="mt-2.5 h-px w-full bg-line/60">
                    <motion.div
                      className={`h-px ${s.dim === top ? "bg-glow" : "bg-slate-500"}`}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(s.total / s.max) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, delay: 0.3 + i * 0.12, ease: EASE }}
                    />
                  </div>
                  {s.summary && s.dim === top && (
                    <p className="mt-3 max-w-2xl text-xs leading-relaxed text-slate-500">{s.summary}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          {/* CTAs */}
          <Reveal delay={0.4}>
            <div className="mt-14 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={() => setState(state === "share" ? "result" : "share")}
                className="btn-primary"
                data-testid="result-share-btn"
              >
                {state === "share" ? "Cancel" : "Share my result"} <Send size={13} />
              </button>
              <Link to="/contact" className="btn-ghost" data-testid="result-contact-cta">
                Talk to Operating Reality <ArrowRight size={13} />
              </Link>
            </div>
            <p className="mt-8 max-w-xl text-xs leading-relaxed text-slate-600">
              This is an orienting instrument, not a verdict. A real diagnosis starts with how the work actually happens — that's the conversation.
            </p>
          </Reveal>

          {/* Share form */}
          {state === "share" && (
            <Reveal delay={0.1}>
              <form
                onSubmit={submitShare}
                className="mt-10 border border-line/40 bg-ink/50 p-8"
                data-testid="result-share-form"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">Share my result</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  Send your diagnostic result to a colleague or contact.
                </p>

                {/* Honeypot */}
                <input type="text" name="website" value={shareForm.website} onChange={e => setShareForm(f => ({ ...f, website: e.target.value }))} className="absolute -left-[9999px] h-0 w-0 opacity-0" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="share-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Recipient email</label>
                    <input id="share-email" type="email" required maxLength={254} value={shareForm.recipient_email} onChange={e => setShareForm(f => ({ ...f, recipient_email: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="colleague@company.com" data-testid="share-recipient-email" />
                  </div>
                  <div>
                    <label htmlFor="share-name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Recipient name <span className="text-slate-700">(optional)</span></label>
                    <input id="share-name" type="text" maxLength={120} value={shareForm.recipient_name} onChange={e => setShareForm(f => ({ ...f, recipient_name: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="Their name" />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="share-msg" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Message <span className="text-slate-700">(optional)</span></label>
                  <textarea id="share-msg" rows={3} maxLength={500} value={shareForm.message} onChange={e => setShareForm(f => ({ ...f, message: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" />
                </div>

                {shareStatus === "sent" ? (
                  <div className="mt-6 flex items-center gap-2 text-sm text-glow" data-testid="share-success">
                    <Check size={14} /> Result sent.
                  </div>
                ) : (
                  <div className="mt-6 flex items-center gap-4">
                    <button type="submit" disabled={shareStatus === "sending"} className="btn-primary disabled:opacity-50" data-testid="share-submit">
                      {shareStatus === "sending" ? <><Loader2 size={13} className="animate-spin" /> Sending</> : <>Send result <ArrowRight size={14} /></>}
                    </button>
                    {shareStatus === "error" && <p className="text-xs text-red-400">Something went wrong. Try again.</p>}
                  </div>
                )}
              </form>
            </Reveal>
          )}

          {/* Consultation CTA */}
          <Reveal delay={0.6}>
            <div className="mt-16 border-t border-line/30 pt-12">
              <p className="kicker">Want us to look at this with you?</p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-400">
                We can help you identify where your operating reality differs from your intended way of working — and where to start.
              </p>
              <Link to="/contact" className="btn-primary mt-8" data-testid="result-talk-cta">
                Talk to Operating Reality <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>

        </div>
      </section>
    </main>
  );
}
